import React, { useEffect, useCallback } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import _get from 'lodash/get';
import { hooks } from '@lifesize/clients.sdk';
import { colors, mixins } from '@lifesize/ux-assets';
import { ReactComponent as PersonIcon } from '@lifesize/ux-assets/vectors/svgIcons/person.svg';
import { ReactComponent as SecureIcon } from '@lifesize/ux-assets/vectors/svgSpecial/secure.svg';
import { ReactComponent as RecordingIcon } from '@lifesize/ux-assets/vectors/svgSpecial/recording.svg';
import { ReactComponent as ChevronBackIcon } from '@lifesize/ux-assets/vectors/svgIcons/chevron-back.svg';
import { ReactComponent as FullScreenEnterIcon } from '@lifesize/ux-assets/vectors/svgIcons/full-screen-enter.svg';
import { makeStyles, Theme, Tooltip } from '@material-ui/core';
import { toggleInCallDrawer } from 'actions/InCall/InCallDrawer';
import InCallNotification from 'components/InCall/InCallNotification/InCallNotification';
import { PARTICIPANTS_DRAWER } from 'constants/InCall/InCallDrawer';
import { toggleMinimized } from 'actions/InCall/InCallVideo';
import { useHistory } from 'react-router-dom';
import useIsMinimized from 'hooks/useIsMinimized';
import { RootState } from 'store/reducers';
import { closeFullScreenModal } from 'actions/FullScreenModal';
import useIsKiosk from 'hooks/useIsKiosk';

const messages = defineMessages({
  participantCount: {
    id: 'components.callHeader.participantCount',
    defaultMessage: 'Active meeting participants'
  },
  recording: {
    id: 'components.callHeader.recording',
    defaultMessage: 'This meeting is being recorded'
  },
  secure: {
    id: 'components.callHeader.secure',
    defaultMessage: 'This meeting is encrypted'
  },
  badCallQualityTitle: {
    id: 'components.inCallNotification.badCallQualityTitle',
    defaultMessage: 'Poor network connection'
  },
  badCallQuality: {
    id: 'components.inCallNotification.badCallQuality',
    defaultMessage: 'Please wait. The video will resume automatically once the network connection improves.'
  },
  offlineTitle: {
    id: 'components.inCallNotification.offlineTitle',
    defaultMessage: 'Trying to reconnect'
  },
  offlineWarning: {
    id: 'components.inCallNotification.offlineWarning',
    defaultMessage: 'Please wait. The network connection was lost but we are working to recover your call.'
  },
  returnToHome: {
    id: 'components.inCall.callHeader.returnToHome',
    defaultMessage: 'Return to Home'
  },
  returnToCall: {
    id: 'components.welcome.returnToCall',
    defaultMessage: 'Return to call'
  }
});

interface Props {
  badCallQuality: boolean;
  intl: { formatMessage: Function };
  isOnline: boolean;
  isReconnecting: boolean;
  title: string;
}

const CallHeader = (props: Props) => {
  const isMinimized = useIsMinimized();
  const isKiosk = useIsKiosk();
  const classes = useStyles({
    ...mixins,
    isMinimized
  });
  const dispatch = useDispatch();
  const isRecording = hooks.useIsRecordingActive();
  const fullScreenModal = useSelector((state: RootState) => state?.app?.modal?.visibleComponent);
  const participantCount = hooks.useParticipants().length;
  const { inCall, inCallActive } = useSelector((state) => _get(state, 'app.notification')) || {}; // fallback provided for Jest
  const participantsDrawerIsOpen =
    useSelector((state) => _get(state, 'app.inCallDrawer.subComponentType')) === PARTICIPANTS_DRAWER;
  const enableReturnToHome = !hooks.useUserInfo()?.isGuest;
  const history = useHistory();

  const onClickParticipants = useCallback(() => {
    if (!participantsDrawerIsOpen) {
      dispatch(toggleInCallDrawer(PARTICIPANTS_DRAWER));
    }
  }, [dispatch, participantsDrawerIsOpen]);

  useEffect(() => {
    if (isMinimized) history.push('/');
  }, [history, isMinimized]);

  const handleReturnToHome = () => {
    if (fullScreenModal) dispatch(closeFullScreenModal());
    dispatch(toggleMinimized());
  };
  return (
    <div className={clsx(classes.container, isMinimized && 'minimizedCallHeader')} data-testid="inCallHeader">
      {!isMinimized && (
        <div className={classes.titleLeft}>
          <div className={classes.expandable}>
            {enableReturnToHome && !isKiosk && (
              <Tooltip
                onClick={handleReturnToHome}
                arrow={true}
                placement="bottom"
                title={props.intl.formatMessage(messages.returnToHome)}
              >
                <ChevronBackIcon className={classes.backIcon} data-testid="chevronBackIcon" />
              </Tooltip>
            )}
            <Tooltip arrow={true} placement="bottom" title={props.intl.formatMessage(messages.secure)}>
              <SecureIcon data-testid="encryptionIcon" />
            </Tooltip>
            {isRecording && !isKiosk && (
              <Tooltip arrow={true} placement="bottom" title={props.intl.formatMessage(messages.recording)}>
                <RecordingIcon data-testid="recordingIcon" />
              </Tooltip>
            )}
            {!isKiosk && (
              <Tooltip arrow={true} placement="bottom" title={props.intl.formatMessage(messages.participantCount)}>
                <div data-testid="participantsCount" className={classes.participants} onClick={onClickParticipants}>
                  <PersonIcon />
                  <span>{participantCount}</span>
                  <div className={clsx(classes.notification, inCallActive && 'isVisible')}>
                    <span>{inCall}</span>
                  </div>
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      )}
      {!isKiosk &&
        (props.title.length > 32 ? (
          <Tooltip
            arrow={true}
            classes={{ tooltip: !isMinimized ? classes.fullTooltip : '' }}
            placement="bottom"
            title={props.title}
          >
            <div data-testid="meetingTitle" className={classes.title}>
              <span>{props.title}</span>
            </div>
          </Tooltip>
        ) : (
          <div data-testid="meetingTitle" className={classes.title}>
            {props.title}
          </div>
        ))}
      {!isMinimized && (
        <div className={classes.titleRight}>
          {props.badCallQuality && props.isOnline ? (
            <InCallNotification errorMessage={props.intl.formatMessage(messages.badCallQualityTitle)}>
              <div>
                <h2 className={classes.badCallQualityTitle}>
                  <FormattedMessage {...messages.badCallQualityTitle} />
                </h2>
                <div className={classes.badCallQualityLine}>
                  <FormattedMessage {...messages.badCallQuality} />
                </div>
              </div>
            </InCallNotification>
          ) : null}
          {!props.isOnline || (!props.badCallQuality && props.isReconnecting) ? (
            <InCallNotification errorMessage={props.intl.formatMessage(messages.offlineTitle)}>
              <div>
                <h2 className={classes.badCallQualityTitle}>
                  <FormattedMessage {...messages.offlineTitle} />
                </h2>
                <div className={classes.badCallQualityLine}>
                  <FormattedMessage {...messages.offlineWarning} />
                </div>
              </div>
            </InCallNotification>
          ) : null}
        </div>
      )}
      {isMinimized && (
        <Tooltip
          arrow
          onClick={() => dispatch(toggleMinimized())}
          placement="right"
          title={props.intl.formatMessage(messages.returnToCall)}
          className={classes.fullScreenIcon}
        >
          <FullScreenEnterIcon data-testid="fullScreenEnterIcon" />
        </Tooltip>
      )}
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    alignItems: 'center',
    color: colors.white,
    display: 'flex',
    flex: '0 0 38px',
    fontWeight: theme.typography.fontWeightLight,
    fontSize: 16,
    position: 'relative',
    width: '100%',
    '&.minimizedCallHeader': {
      fontSize: '1rem',
      '& svg': {
        height: 28,
        width: 'auto'
      }
    }
  },
  title: (mixins: any) => ({
    flex: mixins.isMinimized ? '1 1' : '1 1 auto',
    padding: `0 ${theme.spacing(2)}px`,
    textAlign: 'center',
    ...mixins.ellipsize
  }),
  fullTooltip: (mixins: any) => ({
    // allow the tooltip to expand to the full width of the page if necessary
    maxWidth: mixins.isMinimized ? '50%' : '100%'
  }),
  titleLeft: {
    flex: '0 0 120px',
    height: '100%',
    paddingLeft: theme.spacing(1.5),
    position: 'relative'
  },
  expandable: {
    display: 'flex',
    flexWrap: 'nowrap',
    height: 22,
    position: 'absolute',
    top: 8,
    '& svg': {
      flex: '0 0 auto',
      height: 22,
      marginRight: theme.spacing(1),
      width: 'auto'
    }
  },
  participants: {
    alignItems: 'center',
    border: `1px solid ${colors.white}`,
    borderRadius: 11,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    fontSize: 12,
    height: 22,
    padding: `0 ${theme.spacing(0.75)}px 0 ${theme.spacing(0.5)}px`,
    '& svg': {
      height: 18,
      marginRight: theme.spacing(0.5),
      width: 'auto'
    },
    '& span': {
      whiteSpace: 'nowrap'
    }
  },
  notification: (mixins: any) => ({
    maxWidth: 0,
    paddingLeft: 0,
    transition: 'max-width 1s ease-in-out, padding-left 0s linear 1s',
    width: 'auto',
    ...mixins.ellipsize,
    '&.isVisible': {
      maxWidth: 'calc(100vw - 100px)',
      paddingLeft: theme.spacing(1),
      transition: 'max-width 1s ease-in-out, padding-left 0s linear 0s'
    }
  }),
  titleRight: {
    flex: '0 0 auto',
    minWidth: 120,
    paddingRight: theme.spacing(1.5)
  },
  buttonContainer: {
    width: 28,
    '& button': {
      border: 'none',
      color: '$white-sixty',
      display: 'flex',
      fontSize: '$font-size-header-small',
      margin: 0,
      outline: 'none',
      padding: 0,
      position: 'relative',
      '& svg': {
        height: 28,
        width: 'auto'
      },
      '&:hover': {
        color: colors.white
      }
    }
  },
  badCallQualityLine: {
    color: colors.squidInk
  },
  badCallQualityTitle: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: 12,
    color: colors.squidInk,
    marginBottom: theme.spacing(0.25)
  },
  backIcon: {
    cursor: 'pointer'
  },
  fullScreenIcon: {
    marginLeft: 10,
    cursor: 'pointer'
  }
}));

export default CallHeader;
